import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, FolderTree } from 'lucide-react';
import adminService from '../../services/admin/admin.service';
import type { Template, TemplateField, HierarchyNode } from '../../types/admin/admin.types';
import Table, { type TableColumn } from '../../components/ui/Table';
import EmptyState from '../../components/ui/EmptyState';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import DynamicFieldInput from './DynamicFieldInput';
import { PageHeader, SearchBox, Modal, Field, StatusPill, inputClass, usePagedList } from './ui/AdminUI';
import { nodeLabel } from '../../utils/nodeLabel';
import { useAdminAuthStore } from '../../store/adminAuthStore';

function errorMessage(e: any, fallback: string): string {
  const body = e?.response?.data;
  if (Array.isArray(body?.errors) && body.errors.length > 0) {
    return body.errors
      .map((err: unknown) => {
        if (typeof err === 'string') return err;
        if (err && typeof err === 'object' && 'message' in err) return String((err as { message: unknown }).message);
        return String(err);
      })
      .join(' ');
  }
  return body?.message || fallback;
}

const HierarchyBuilderPage: React.FC = () => {
  const { hasTemplate } = useAdminAuthStore();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [template, setTemplate] = useState<Template | null>(null);
  const [instances, setInstances] = useState<HierarchyNode[]>([]);
  const [parentOptions, setParentOptions] = useState<HierarchyNode[]>([]);
  const [refOptions, setRefOptions] = useState<Record<number, Array<{ id: number; label: string }>>>({});
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editNode, setEditNode] = useState<HierarchyNode | null>(null);
  const [treeNode, setTreeNode] = useState<HierarchyNode | null>(null);
  const [tree, setTree] = useState<HierarchyNode[]>([]);

  useEffect(() => {
    adminService.listTemplates({ is_active: true, is_hierarchy_node: true }).then((r) => setTemplates(r.data));
  }, []);

  const visibleTemplates = useMemo(
    () => templates.filter((t) => hasTemplate(t.template_key, 'view')),
    [templates, hasTemplate]
  );

  const loadForTemplate = useCallback(async (id: number) => {
    setLoading(true);
    try {
      const tpl = (await adminService.getTemplate(id)).data;
      setTemplate(tpl);
      setInstances((await adminService.listNodes({ template_id: id, limit: 200 })).data);
      setParentOptions(tpl.parent_template_id ? (await adminService.listNodes({ template_id: tpl.parent_template_id, is_active: true, limit: 200 })).data : []);
      const refs: Record<number, Array<{ id: number; label: string }>> = {};
      for (const field of tpl.fields || []) {
        if (field.field_type === 'reference' && field.validation_rules?.referenced_template_id) {
          const opts = await adminService.listNodes({ template_id: field.validation_rules.referenced_template_id, is_active: true, limit: 200 });
          refs[field.id] = opts.data.map((n) => ({ id: n.id, label: nodeLabel(n) }));
        }
      }
      setRefOptions(refs);
    } catch (e: any) {
      toast.error(errorMessage(e, 'Failed to load instances.'));
      setTemplate(null);
      setInstances([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (templateId) loadForTemplate(templateId); }, [templateId, loadForTemplate]);

  const activeFields = useMemo(() => (template?.fields || []).filter((f) => f.is_active).sort((a, b) => a.display_order - b.display_order), [template]);
  const ready = template && template.id === templateId && !loading;
  const canCreate = template ? hasTemplate(template.template_key, 'create') : false;
  const canEdit = template ? hasTemplate(template.template_key, 'edit') : false;
  const canDelete = template ? hasTemplate(template.template_key, 'delete') : false;
  const canView = template ? hasTemplate(template.template_key, 'view') : false;
  const showTreeAction = canView;

  const { query, setQuery, pageItems, pagination, setPage } = usePagedList<HierarchyNode>(
    instances, (n, q) => nodeLabel(n).toLowerCase().includes(q) || (n.path || '').includes(q), 10
  );

  const showTree = async (node: HierarchyNode) => {
    try {
      setTreeNode(node);
      setTree((await adminService.nodeTree(node.id, { include_inactive: true, limit: 500 })).data);
    } catch (e: any) {
      // Fallback: if API denies but row is already visible in this table, show local subtree.
      const prefix = node.path || '';
      const localTree = instances.filter((n) => (n.path || '').startsWith(prefix));
      if (localTree.length > 0) {
        setTreeNode(node);
        setTree(localTree);
        return;
      }
      setTreeNode(null);
      setTree([]);
      toast.error(errorMessage(e, 'Failed to load subtree.'));
    }
  };

  const toggleStatus = async (n: HierarchyNode) => {
    try { await adminService.setNodeStatus(n.id, !n.is_active, true); loadForTemplate(templateId!); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Failed.'); }
  };

  useEffect(() => {
    if (templateId && !visibleTemplates.some((t) => t.id === templateId)) {
      setTemplateId(null);
      setTemplate(null);
      setInstances([]);
    }
  }, [templateId, visibleTemplates]);

  const columns: TableColumn<HierarchyNode>[] = [
    { key: 'name', header: 'Name', render: (n) => <span className="font-medium text-[var(--color-gray-800)]">{nodeLabel(n)}</span> },
    { key: 'path', header: 'Path', render: (n) => <span className="text-mono text-xs text-[var(--color-gray-400)]">{n.path}</span> },
    { key: 'status', header: 'Status', align: 'center', render: (n) => <StatusPill active={n.is_active} /> },
    { key: 'actions', header: '', align: 'right', width: 220, render: (n) => (
      <div className="flex items-center justify-end gap-1">
        {showTreeAction && (
          <button title="Tree" className="p-1.5 rounded hover:bg-[var(--color-gray-100)] text-[var(--color-gray-500)]" onClick={(e) => { e.stopPropagation(); showTree(n); }}><FolderTree size={15} /></button>
        )}
        {canEdit && (
          <button title="Edit" className="p-1.5 rounded hover:bg-[var(--color-gray-100)] text-[var(--color-gray-500)]" onClick={(e) => { e.stopPropagation(); setEditNode(n); }}><Pencil size={15} /></button>
        )}
        {canDelete && (
          <button className="px-2 py-1 rounded hover:bg-[var(--color-gray-100)] text-xs text-[var(--color-gray-600)]" onClick={(e) => { e.stopPropagation(); toggleStatus(n); }}>{n.is_active ? 'Deactivate' : 'Activate'}</button>
        )}
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Hierarchy Builder" subtitle="Create and organise instances of your templates"
        actions={ready && canCreate ? <Button size="sm" onClick={() => setCreateOpen(true)}><Plus size={16} className="mr-1" /> New {template!.name}</Button> : undefined} />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select className={`${inputClass} max-w-xs`} value={templateId ?? ''} onChange={(e) => { setTemplateId(e.target.value ? Number(e.target.value) : null); setTreeNode(null); }}>
          <option value="">— choose a template —</option>
          {visibleTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {templateId && <div className="max-w-xs flex-1 min-w-[200px]"><SearchBox value={query} onChange={setQuery} placeholder="Search instances…" /></div>}
      </div>

      {visibleTemplates.length === 0 ? (
        <EmptyState title="No templates available" description="Your role has no view permission on any hierarchy template." />
      ) : !templateId ? (
        <EmptyState title="Choose a template" description="Pick a template above to create and view its instances." />
      ) : !ready ? (
        <div className="p-10 flex justify-center"><Spinner size={28} /></div>
      ) : (
        <Table columns={columns} rows={pageItems} rowKey={(n) => String(n.id)} minWidth={640} onRowClick={canEdit ? (n) => setEditNode(n) : undefined}
          pagination={pagination} onPageChange={setPage}
          emptyState={<EmptyState title="No instances yet" description={`Create the first ${template!.name}.`} />} />
      )}

      {ready && canCreate && (
        <NodeFormModal open={createOpen} mode="create" template={template!} fields={activeFields} parentOptions={parentOptions} refOptions={refOptions}
          onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); loadForTemplate(templateId!); }} />
      )}
      {ready && canEdit && editNode && (
        <NodeFormModal open mode="edit" node={editNode} template={template!} fields={activeFields} parentOptions={parentOptions} refOptions={refOptions}
          onClose={() => setEditNode(null)} onSaved={() => { setEditNode(null); loadForTemplate(templateId!); }} />
      )}

      {treeNode && (
        <Modal open title={`Subtree — ${nodeLabel(treeNode)}`} onClose={() => setTreeNode(null)} width="md"
          footer={<Button size="sm" variant="secondary" onClick={() => setTreeNode(null)}>Close</Button>}>
          <ul className="text-sm max-h-[55vh] overflow-y-auto">
            {tree.map((n) => {
              const depth = Math.max(0, (n.path.match(/\//g) || []).length - 2);
              return (
                <li key={n.id} style={{ paddingLeft: `${depth * 18}px` }} className="py-1 flex items-center gap-2 border-b border-[var(--color-gray-50)]">
                  <span className="text-[var(--color-gray-400)]">{depth > 0 ? '└' : '●'}</span>
                  <span className="text-[var(--color-gray-800)]">{nodeLabel(n)}</span>
                  <span className="text-xs text-[var(--color-gray-400)]">{n.template?.name}</span>
                  {!n.is_active && <StatusPill active={false} />}
                </li>
              );
            })}
          </ul>
        </Modal>
      )}
    </div>
  );
};

const NodeFormModal: React.FC<{
  open: boolean; mode: 'create' | 'edit'; node?: HierarchyNode; template: Template; fields: TemplateField[];
  parentOptions: HierarchyNode[]; refOptions: Record<number, Array<{ id: number; label: string }>>;
  onClose: () => void; onSaved: () => void;
}> = ({ open, mode, node, template, fields, parentOptions, refOptions, onClose, onSaved }) => {
  const [data, setData] = useState<Record<string, unknown>>({});
  const [parentId, setParentId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(mode === 'edit' && node ? { ...node.data } : {});
    setParentId(mode === 'edit' && node?.parent_node_id ? String(node.parent_node_id) : '');
  }, [open, mode, node]);

  const submit = async () => {
    if (template.parent_template_id && !parentId) return toast.error('Select a parent.');
    setSaving(true);
    try {
      if (mode === 'create') {
        await adminService.createNode({ template_id: template.id, parent_node_id: parentId ? Number(parentId) : null, data });
      } else if (node) {
        await adminService.updateNode(node.id, { data, ...(template.parent_template_id ? { parent_node_id: parentId ? Number(parentId) : null } : {}) });
      }
      toast.success(`Instance ${mode === 'create' ? 'created' : 'updated'}.`); onSaved();
    } catch (e: any) {
      toast.error(errorMessage(e, 'Failed to save.'));
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} title={`${mode === 'create' ? 'New' : 'Edit'} ${template.name}`} onClose={onClose} width="md"
      footer={<><Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button><Button size="sm" onClick={submit} disabled={saving}>{saving ? <Spinner size={14} /> : 'Save'}</Button></>}>
      <div className="space-y-3">
        {template.parent_template_id && (
          <Field label="Parent" required>
            <select className={inputClass} value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">— select parent —</option>
              {parentOptions.map((p) => <option key={p.id} value={p.id}>{nodeLabel(p)}</option>)}
            </select>
          </Field>
        )}
        {fields.map((f) => (
          <DynamicFieldInput key={f.id} field={f} value={data[f.field_key]} referenceOptions={refOptions[f.id] || []} onChange={(v) => setData({ ...data, [f.field_key]: v })} />
        ))}
        {fields.length === 0 && <p className="text-sm text-[var(--color-gray-500)]">This template has no active fields.</p>}
      </div>
    </Modal>
  );
};

export default HierarchyBuilderPage;
